import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAwardMetalsConfig, saveAwardMetalsConfig, DEFAULT_METAL_COLORS } from '../../hooks/useAwardMetalsConfig';
import { ConfirmDialog } from '../ui/ConfirmDialog';

// Shipped rank order — bronze is the entry tier, diamond the top. Keep this in
// sync with the RAMP in awardMetals.js; it is presentation order only, not a
// second source of truth for the ramp itself.
const METAL_ORDER = ['bronze', 'silver', 'gold', 'onyx', 'platinum', 'obsidian', 'diamond'];

// The two dark-plate metals that override the global --sheen (see index.css).
// Every other metal has no sheen field at all — out of scope, not just empty.
const METALS_WITH_SHEEN = new Set(['onyx', 'obsidian']);

const FIELDS = ['face', 'ring', 'ink', 'deep'];
const THEMES = ['light', 'dark'];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
// Numeric channels, 0-3 digit r/g/b and an alpha that's either 0, 1, or a
// decimal — matches the DEFAULT_METAL_COLORS shape (e.g. "rgba(255,255,255,.20)")
// while still rejecting free text before it reaches the backend.
const RGBA_RE = /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|\.\d+|0?\.\d+)\s*\)$/;

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function validateHex(value) {
  return HEX_RE.test(value ?? '') ? null : 'Must be #RRGGBB';
}

function validateRgba(value) {
  return RGBA_RE.test(value ?? '') ? null : 'Must be rgba(r,g,b,a)';
}

/** Swatch + hex text input pair — same pattern as the banner gradient editor
 * in EditProfileModal.jsx, reused here for visual consistency. */
function HexField({ label, value, onChange, error }) {
  return (
    <div className="flex flex-col gap-1 min-w-[104px]">
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={HEX_RE.test(value) ? value : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded-md border border-line cursor-pointer flex-none p-0 bg-transparent"
          aria-label={`${label} swatch`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          aria-label={`${label} hex value`}
          className={`w-[76px] px-1.5 py-1 text-[11px] font-mono rounded-md border bg-surface text-ink
            focus:outline-none focus:ring-2
            ${error ? 'border-red-400 focus:ring-red-400/40' : 'border-line focus:ring-accent-line'}`}
        />
      </div>
      {error && <p className="text-[10px] text-red-500 leading-tight">{error}</p>}
    </div>
  );
}

/** rgba isn't a valid `<input type="color">` value, so sheen is text-only with
 * an inline preview swatch shown over a dark checkered chip. */
function SheenField({ label, value, onChange, error }) {
  const valid = RGBA_RE.test(value);
  return (
    <div className="flex flex-col gap-1 min-w-[150px]">
      <div className="flex items-center gap-1.5">
        <div
          className="w-6 h-6 rounded-md border border-line flex-none overflow-hidden"
          style={{
            backgroundImage:
              'repeating-conic-gradient(#2a2a2a 0% 25%, #171717 0% 50%)',
            backgroundSize: '8px 8px',
          }}
          aria-hidden
        >
          <div className="w-full h-full" style={{ background: valid ? value : 'transparent' }} />
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          aria-label={`${label} sheen value`}
          className={`w-[122px] px-1.5 py-1 text-[11px] font-mono rounded-md border bg-surface text-ink
            focus:outline-none focus:ring-2
            ${error ? 'border-red-400 focus:ring-red-400/40' : 'border-line focus:ring-accent-line'}`}
        />
      </div>
      {error && <p className="text-[10px] text-red-500 leading-tight">{error}</p>}
    </div>
  );
}

export function AwardMetalsSettings() {
  const config = useAwardMetalsConfig();

  // A local working copy so invalid in-progress edits (an admin mid-way
  // through typing a hex value) never touch localStorage or the live CSS
  // custom properties until Save is clicked and validation passes.
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(config)));
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saved
  const saveTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  const errors = useMemo(() => {
    const out = {};
    for (const theme of THEMES) {
      out[theme] = {};
      for (const metal of METAL_ORDER) {
        const rowErrors = {};
        for (const field of FIELDS) {
          const msg = validateHex(draft?.[theme]?.[metal]?.[field]);
          if (msg) rowErrors[field] = msg;
        }
        if (METALS_WITH_SHEEN.has(metal)) {
          const msg = validateRgba(draft?.[theme]?.[metal]?.sheen);
          if (msg) rowErrors.sheen = msg;
        }
        out[theme][metal] = rowErrors;
      }
    }
    return out;
  }, [draft]);

  const hasErrors = useMemo(
    () => THEMES.some((theme) => METAL_ORDER.some((metal) => Object.keys(errors[theme][metal]).length > 0)),
    [errors],
  );

  const setField = (theme, metal, field, value) => {
    setSaveState('idle');
    setDraft((prev) => ({
      ...prev,
      [theme]: {
        ...prev[theme],
        [metal]: { ...prev[theme][metal], [field]: value },
      },
    }));
  };

  const resetMetalRow = (metal) => {
    setSaveState('idle');
    setDraft((prev) => {
      const next = { ...prev };
      for (const theme of THEMES) {
        next[theme] = { ...prev[theme], [metal]: { ...DEFAULT_METAL_COLORS[theme][metal] } };
      }
      return next;
    });
  };

  const resetAll = () => {
    setDraft(JSON.parse(JSON.stringify(DEFAULT_METAL_COLORS)));
    setConfirmResetAll(false);
    setSaveState('idle');
  };

  const handleSave = () => {
    if (hasErrors) return;
    saveAwardMetalsConfig(draft);
    setSaveState('saved');
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveState('idle'), 2000);
  };

  return (
    <div className="space-y-3.5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-[16px] font-bold text-ink mb-[3px]">Service Award Colors</h3>
          <p className="text-[12.5px] text-ink-3 leading-[1.5] max-w-[62ch]">
            The crest ramp used for tenure badges — bronze through diamond, light and dark theme.
            Changes apply everywhere the ramp renders (Trophy Case, MOREanniversaries, the
            badge-earned celebration) once saved.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-none">
          {saveState === 'saved' && (
            <span className="text-[12px] font-semibold text-teal-600 dark:text-teal-400">✓ Saved</span>
          )}
          {hasErrors && saveState !== 'saved' && (
            <span className="text-[11px] font-medium text-red-500">Fix invalid values before saving</span>
          )}
          <button
            onClick={() => setConfirmResetAll(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0
                       text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-900/40"
          >
            Reset all
          </button>
          <button
            onClick={handleSave}
            disabled={hasErrors}
            className="h-[30px] px-3.5 text-[12px] font-semibold rounded-ctl bg-accent text-[var(--on-accent)]
                       disabled:opacity-45 disabled:cursor-not-allowed"
          >
            Save
          </button>
        </div>
      </div>

      <div className="rounded-card border border-line bg-surface shadow-e1 overflow-x-auto">
        <table className="border-collapse w-full">
          <thead>
            <tr className="bg-surface-2">
              <th rowSpan={2} className="sticky left-0 z-10 bg-surface-2 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-ink-4 border-b border-r border-line">
                Metal
              </th>
              <th colSpan={5} className="px-3 py-1.5 text-center text-[11px] font-bold uppercase tracking-[0.08em] text-ink-4 border-b border-line">
                Light
              </th>
              <th colSpan={5} className="px-3 py-1.5 text-center text-[11px] font-bold uppercase tracking-[0.08em] text-ink-4 border-b border-l border-line">
                Dark
              </th>
              <th rowSpan={2} className="px-3 py-2 border-b border-l border-line" />
            </tr>
            <tr className="bg-surface-2">
              {THEMES.map((theme) => (
                [...FIELDS, 'sheen'].map((field) => (
                  <th
                    key={`${theme}-${field}`}
                    className={`px-3 py-1.5 text-left text-[10.5px] font-semibold text-ink-3 border-b border-line
                      ${theme === 'dark' && field === 'face' ? 'border-l' : ''}`}
                  >
                    {cap(field)}
                  </th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {METAL_ORDER.map((metal, i) => {
              const hasSheen = METALS_WITH_SHEEN.has(metal);
              return (
                <tr key={metal} className={i > 0 ? 'border-t border-line' : ''}>
                  <td className="sticky left-0 z-10 bg-surface px-3 py-2 text-[12.5px] font-semibold text-ink border-r border-line whitespace-nowrap">
                    {cap(metal)}
                  </td>
                  {THEMES.map((theme) => (
                    <Fragment key={`${theme}-${metal}`}>
                      {FIELDS.map((field) => (
                        <td key={`${theme}-${metal}-${field}`} className={`px-3 py-2 align-top ${theme === 'dark' && field === 'face' ? 'border-l border-line' : ''}`}>
                          <HexField
                            label={`${metal} ${theme} ${field}`}
                            value={draft[theme][metal][field] ?? ''}
                            onChange={(v) => setField(theme, metal, field, v)}
                            error={errors[theme][metal][field]}
                          />
                        </td>
                      ))}
                      <td key={`${theme}-${metal}-sheen`} className="px-3 py-2 align-top">
                        {hasSheen ? (
                          <SheenField
                            label={`${metal} ${theme} sheen`}
                            value={draft[theme][metal].sheen ?? ''}
                            onChange={(v) => setField(theme, metal, 'sheen', v)}
                            error={errors[theme][metal].sheen}
                          />
                        ) : (
                          <span className="text-[11px] text-ink-4">—</span>
                        )}
                      </td>
                    </Fragment>
                  ))}
                  <td className="px-3 py-2 align-top border-l border-line">
                    <button
                      onClick={() => resetMetalRow(metal)}
                      className="text-[11px] font-semibold text-ink-3 hover:text-accent transition-colors whitespace-nowrap"
                    >
                      Reset
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmResetAll}
        title="Reset all award colors to shipped defaults?"
        body="Reverts every field, both themes, back to the values PeopleVUE ships with. This only clears the draft below — nothing changes for other users until you click Save."
        confirmLabel="Reset all"
        onConfirm={resetAll}
        onCancel={() => setConfirmResetAll(false)}
      />
    </div>
  );
}
